import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getFornitori, deleteFornitore, exportFornitoriCSV, deleteMultipleFornitori, exportFornitoriExcel, getLayoutsByModulo, generatePdf } from '../services/api';
import { Table, Form, Spinner, Alert, Button, Row, Col, Dropdown, Pagination, Card, InputGroup, Badge } from 'react-bootstrap'; // Aggiunto InputGroup e Badge
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import ActionToolbar from '../common/ActionToolbar';
import MobileListLayout from '../common/MobileListLayout';
import { usePageTitle } from '../context/PageTitleContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faTruck, faBuilding, faPhone, faEnvelope, faMapMarkerAlt, faIdCard, faPlus, faFileExport, faFileImport, faPrint } from '@fortawesome/free-solid-svg-icons'; // Aggiunto faFilter e altre icone
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import SortController from '../common/SortController';
import SortableHeader from '../common/SortableHeader';
import ColumnOrderController from '../common/ColumnOrderController';

const columnHelper = createColumnHelper();

const allColumnsMap = {
    id: "ID",
    denominazione: "Denominazione",
    codice_fiscale: "Codice Fiscale",
    partita_iva: "Partita IVA",
    indirizzo: "Indirizzo",
    cap: "CAP",
    citta: "Città",
    provincia: "Provincia",
    nazione: "Nazione",
    codice_destinatario: "Codice Destinatario",
    telefono: "Telefono",
    cellulare: "Cellulare",
    email: "Email",
    pec: "PEC",
    luogo_nascita: "Luogo di Nascita",
    data_nascita: "Data di Nascita",
};

const initialVisibleColumns = ['id', 'denominazione', 'partita_iva', 'email', 'telefono', 'citta', 'provincia'];

const visibilityKey = 'table-visibility-fornitori';

function FornitoriList() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFornitoreIds, setSelectedFornitoreIds] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [distinctValues, setDistinctValues] = useState({ // Aggiunto per i facet
    nazione: [],
    provincia: [],
    citta: [],
  });
  const [quickSearch, setQuickSearch] = useState(() => sessionStorage.getItem('list-quicksearch-fornitori') || '');

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem(visibilityKey);
    if (saved) return JSON.parse(saved);
    return Object.keys(allColumnsMap).reduce((acc, key) => {
      acc[key] = initialVisibleColumns.includes(key);
      return acc;
    }, {});
  });
  
  const [sorting, setSorting] = useState(() => {
    const saved = sessionStorage.getItem('list-sorting-fornitori');
    return saved ? JSON.parse(saved) : [];
  });

  const [columnFilters, setColumnFilters] = useState(() => {
    const saved = sessionStorage.getItem('list-filters-fornitori');
    return saved ? JSON.parse(saved) : [];
  });

  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem('table-column-order-fornitori');
    const defaultOrder = ['select', ...Object.keys(allColumnsMap)];
    if (savedOrder) {
        let parsedOrder = JSON.parse(savedOrder);
        parsedOrder = parsedOrder.filter(c => c !== 'select');
        return ['select', ...parsedOrder];
    }
    return defaultOrder;
  });

  const [columnSizing, setColumnSizing] = useState(() => {
    const saved = localStorage.getItem('table-column-sizing-fornitori');
    return saved ? JSON.parse(saved) : {};
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalFornitori, setTotalFornitori] = useState(0);
  const [reportType, setReportType] = useState('all'); // all, with_piva, without_piva
  const [, setReportTitle] = useState('Tutti i Fornitori');

  const fetchFornitoriData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = columnFilters.reduce((acc, filter) => {
            const { id, value } = filter;
            if (value && typeof value === 'object' && !Array.isArray(value) && value.__advanced) {
                if (value.__advanced === 'not_empty') acc[`${id}__isnot`] = null;
                else if (value.__advanced === 'contains') acc[`${id}__ilike`] = value.text;
                else if (value.__advanced === 'not_contains') acc[`${id}__not_ilike`] = value.text;
            } else if (Array.isArray(value)) {
                if (value.length > 0) {
                    acc[id] = { 'in': value };
                }
            } else if (typeof value === 'string' && value.toUpperCase() === 'N/A') {
                acc[id] = null;
            } else {
                acc[id] = value;
            }
            return acc;
        }, {});

      const sortParams = sorting.map(sort => `${sort.id}:${sort.desc ? 'desc' : 'asc'}`);
      const stringifiedFilters = JSON.stringify(filters);

      const response = await getFornitori((currentPage - 1) * itemsPerPage, itemsPerPage, sortParams, stringifiedFilters);
      setFornitori(response.data.fornitori);
      setTotalFornitori(response.data.total);
      if (response.data.facets) { // Assicurati che il backend restituisca i facet
        setDistinctValues(response.data.facets);
      }
    } catch (err) {
      setError('Errore nel caricamento dei fornitori.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, sorting, columnFilters]);

  useEffect(() => {
    fetchFornitoriData();
  }, [fetchFornitoriData]);



  // Nuovo useEffect per gestire reportType (copiato e adattato da ClientiList)
  useEffect(() => {
    setColumnFilters(prevFilters => {
      const otherFilters = prevFilters.filter(f => f.id !== 'partita_iva'); // Rimuovi il filtro precedente se esiste
      if (reportType === 'with_piva') {
        return [...otherFilters, { id: 'partita_iva', value: { ne: null } }]; // Non nullo
      }
      if (reportType === 'without_piva') {
        return [...otherFilters, { id: 'partita_iva', value: null }]; // Nullo
      }
      return otherFilters;
    });

    if (reportType === 'with_piva') {
      setReportTitle('Fornitori con Partita IVA');
    } else if (reportType === 'without_piva') {
      setReportTitle('Fornitori senza Partita IVA');
    } else {
      setReportTitle('Tutti i Fornitori');
    }
  }, [reportType]);

  const getColumnValue = (fornitore, columnName) => {
    switch (columnName) {
      case 'data_nascita':
        return fornitore.data_nascita ? new Date(fornitore.data_nascita).toLocaleDateString() : 'N/A';
      default:
        return fornitore[columnName] != null ? String(fornitore[columnName]) : 'N/A';
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => <Form.Check type="checkbox" onChange={table.getToggleAllRowsSelectedHandler()} checked={table.getIsAllRowsSelected()} disabled={fornitori.length === 0} />,
        cell: ({ row }) => <Form.Check type="checkbox" checked={row.getIsSelected()} onChange={row.getToggleSelectedHandler()} onClick={(e) => e.stopPropagation()} />,
        minSize: 30, maxSize: 30, size: 30, enableResizing: false, enableSorting: false, enableColumnFilter: false,
      }),
      ...Object.entries(allColumnsMap).map(([key, value]) =>
        columnHelper.accessor(key, {
          header: value,
          cell: info => getColumnValue(info.row.original, key),
          enableColumnFilter: true,
          meta: {
            filterVariant: ['nazione', 'provincia', 'citta'].includes(key) ? 'select' : 'text', // Aggiunti filtri select per nazione, provincia, città
            filterOptions: distinctValues[key], // Utilizza distinctValues per popolare le opzioni
          },
        })
      ),
    ],
    [fornitori, distinctValues] // Aggiungi distinctValues alle dipendenze
  );

  const onColumnVisibilityChange = (updater) => {
    const newState = typeof updater === 'function' ? updater(columnVisibility) : updater;
    setColumnVisibility(newState);
    localStorage.setItem(visibilityKey, JSON.stringify(newState));
  };

  const table = useReactTable({ 
    data: fornitori, columns, state: { columnVisibility, sorting, columnFilters, columnOrder, columnSizing },
    onColumnVisibilityChange, onSortingChange: setSorting, onColumnFiltersChange: setColumnFilters, onColumnOrderChange: setColumnOrder, onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(), getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(), getFacetedUniqueValues: getFacetedUniqueValues(),
    enableColumnResizing: true, columnResizeMode: 'onChange', enableColumnReordering: true, manualFiltering: true,
  });

  const handleAutoResize = (column) => {
    const headerText = String(column.columnDef.header);
    const cells = table.getRowModel().rows.map(row => String(row.original[column.id] ?? ''));
    const charMultiplier = 8, padding = 30;
    const headerWidth = headerText.length * charMultiplier;
    const maxWidth = cells.reduce((max, cell) => Math.max(max, cell.length * charMultiplier), headerWidth);
    const newSize = Math.ceil(maxWidth) + padding;
    table.setColumnSizing(prev => ({ ...prev, [column.id]: newSize }));
  };

  useEffect(() => {
    sessionStorage.setItem('list-sorting-fornitori', JSON.stringify(sorting));
  }, [sorting]);

  useEffect(() => {
    sessionStorage.setItem('list-filters-fornitori', JSON.stringify(columnFilters));
  }, [columnFilters]);

  useEffect(() => {
    sessionStorage.setItem('list-quicksearch-fornitori', quickSearch);
    const timer = setTimeout(() => {
      setColumnFilters(prev => {
        const without = prev.filter(f => f.id !== '_global_search');
        return quickSearch ? [...without, { id: '_global_search', value: quickSearch }] : without;
      });
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [quickSearch]);

  useEffect(() => {
    sessionStorage.setItem('list-currentPage-fornitori', JSON.stringify(currentPage));
  }, [currentPage]);

  useEffect(() => {
    sessionStorage.setItem('list-itemsPerPage-fornitori', JSON.stringify(itemsPerPage));
  }, [itemsPerPage]);

  const { rows } = table.getSelectedRowModel();
  useEffect(() => {
    const newSelectedIds = rows
      .map((row) => row.original.id)
      .filter((id) => id != null);
    setSelectedFornitoreIds(newSelectedIds);
  }, [rows]);

  const handleRowClick = useCallback((row) => {
    sessionStorage.setItem('list-currentPage-fornitori', JSON.stringify(currentPage));
    sessionStorage.setItem('list-itemsPerPage-fornitori', JSON.stringify(itemsPerPage));
    sessionStorage.setItem('list-sorting-fornitori', JSON.stringify(sorting));
    sessionStorage.setItem('list-filters-fornitori', JSON.stringify(columnFilters));
    
    navigate(`/fornitori/${row.original.id}`);
  }, [currentPage, itemsPerPage, sorting, columnFilters, navigate]);

  const createDownloadLink = (response, fileName) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportSelected = async () => {
    setExporting(true);
    try {
      const response = await exportFornitoriCSV(selectedFornitoreIds);
      createDownloadLink(response, 'fornitori_selezionati.csv');
      table.toggleAllRowsSelected(false);
    } catch (err) { setError('Errore durante l\'esportazione.'); }
    finally { setExporting(false); }
  };

  const handleExportAllExcel = async () => {
    setExportingExcel(true);
    try {
      const response = await exportFornitoriExcel();
      createDownloadLink(response, 'fornitori.xlsx');
    } catch (err) { setError('Errore durante l\'esportazione.'); }
    finally { setExportingExcel(false); }
  };

  const handlePrintWithLayout = async (layoutId) => {
    if (selectedFornitoreIds.length === 0) return toast.error("Seleziona almeno un fornitore per la stampa.");
    setLoading(true);
    try {
      const response = await generatePdf(layoutId, selectedFornitoreIds);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `documento_layout_${layoutId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) { setError("Errore durante la generazione del PDF."); }
    finally { setLoading(false); }
  };

  const handleResetFilters = () => {
    table.resetColumnFilters();
    setSorting([]);
    setReportType('all');
    setQuickSearch('');
  };

  const deleteApiMap = { fornitori: deleteFornitore };
  const deleteMultipleApiMap = { fornitori: deleteMultipleFornitori };

  const renderPaginationItems = () => {
    const totalPages = Math.ceil(totalFornitori / itemsPerPage);
    const pageNumbers = []; const maxPagesToShow = 5; let startPage, endPage;
    if (totalPages <= maxPagesToShow) { startPage = 1; endPage = totalPages; }
    else {
      if (currentPage <= Math.ceil(maxPagesToShow / 2)) { startPage = 1; endPage = maxPagesToShow; }
      else if (currentPage + Math.floor(maxPagesToShow / 2) >= totalPages) { startPage = totalPages - maxPagesToShow + 1; endPage = totalPages; }
      else { startPage = currentPage - Math.floor(maxPagesToShow / 2); endPage = currentPage + Math.floor(maxPagesToShow / 2); }
    }
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>{i}</Pagination.Item>);
    }
    return pageNumbers;
  };

  const hasManualFilters = table.getState().columnFilters.length > 0;

  // Render function per le card mobile
  const renderFornitoreCard = useCallback((fornitore, isSelected, onToggleSelect) => {
    const fullName = fornitore.denominazione || 'Fornitore senza denominazione';
    
    return (
      <div className="d-flex align-items-start">
        <Form.Check
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onToggleSelect && onToggleSelect(e)}
          className="me-3"
          style={{ marginTop: '2px' }}
        />
        
        <div className="flex-grow-1" onClick={() => navigate(`/fornitori/edit/${fornitore.id}`)} style={{ cursor: 'pointer' }}>
          <div className="d-flex align-items-center mb-1">
            <FontAwesomeIcon icon={faTruck} className="text-primary me-2" />
            <h6 className="mb-0 fw-bold">{fullName}</h6>
          </div>
          
          <small className="text-muted d-block mb-2">
            ID: {fornitore.id}
            {fornitore.partita_iva && ` • P.IVA: ${fornitore.partita_iva}`}
          </small>
          
          <div>
            {fornitore.email && (
              <Badge bg="info" className="me-1" style={{ fontSize: '0.65rem' }}>
                <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                {fornitore.email}
              </Badge>
            )}
            {fornitore.telefono && (
              <Badge bg="secondary" className="me-1" style={{ fontSize: '0.65rem' }}>
                <FontAwesomeIcon icon={faPhone} className="me-1" />
                {fornitore.telefono}
              </Badge>
            )}
            {fornitore.codice_fiscale && (
              <Badge bg="primary" className="me-1" style={{ fontSize: '0.65rem' }}>
                <FontAwesomeIcon icon={faIdCard} className="me-1" />
                CF: {fornitore.codice_fiscale}
              </Badge>
            )}
            {(fornitore.citta || fornitore.provincia) && (
              <Badge bg="success" style={{ fontSize: '0.65rem' }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                {[fornitore.citta, fornitore.provincia].filter(Boolean).join(', ')}
              </Badge>
            )}
          </div>
        </div>
      </div>
    );
  }, [navigate]);

  // Quick filters per mobile (FornitoriList non ha filtri specifici come reportType)
  const quickFilters = useMemo(() => [
    {
      label: 'Tutti',
      icon: faTruck,
      isActive: true,
      onClick: () => {},
      gradients: ['#6c757d', '#495057']
    }
  ], []);

  // Actions dropdown content
  const actionsDropdownContent = useMemo(() => (
    <>
      <div className="px-3 py-2 border-bottom">
        <small className="text-muted fw-bold">ESPORTAZIONE</small>
      </div>
      <Dropdown.Item onClick={handleExportSelected} disabled={selectedFornitoreIds.length === 0 || exporting}>
        <FontAwesomeIcon icon={faFileExport} className="me-2" />
        {exporting ? 'Esportazione...' : 'Esporta Selezionati (CSV)'}
      </Dropdown.Item>
      <Dropdown.Item onClick={handleExportAllExcel} disabled={exportingExcel}>
        <FontAwesomeIcon icon={faFileExport} className="me-2" />
        {exportingExcel ? 'Esportazione...' : 'Esporta Tutto (Excel)'}
      </Dropdown.Item>
      
      <div className="px-3 py-2 border-bottom border-top">
        <small className="text-muted fw-bold">STAMPA</small>
      </div>
      <Dropdown.Item onClick={() => handlePrintWithLayout(1)} disabled={selectedFornitoreIds.length === 0}>
        <FontAwesomeIcon icon={faPrint} className="me-2" />
        Stampa con Layout
      </Dropdown.Item>
    </>
  ), [exporting, exportingExcel, selectedFornitoreIds.length, handleExportSelected, handleExportAllExcel, handlePrintWithLayout]);

  usePageTitle(`Fornitori (${totalFornitori})`);

  return (
    <>
    <Card>
      <Card.Header className="sticky-card-header">
        {isMobile ? (
          <MobileListLayout
            entityName="Fornitore"
            entityNamePlural="Fornitori"
            createPath="/nuovo/fornitore"
            createButtonText="Nuovo Fornitore"
            quickFilters={quickFilters}
            actionsDropdownContent={actionsDropdownContent}
            renderCard={renderFornitoreCard}
            items={table.getRowModel().rows.map(row => row.original)}
            selectedIds={selectedFornitoreIds}
            onToggleSelect={(fornitoreId) => {
              const row = table.getRowModel().rows.find(r => r.original.id === fornitoreId);
              if (row) row.toggleSelected();
            }}
            onCardClick={(fornitore) => navigate(`/fornitori/edit/${fornitore.id}`)}
            onDeleteMultiple={() => {
              if (window.confirm(`Vuoi eliminare ${selectedFornitoreIds.length} fornitore/i selezionato/i?`)) {
                deleteMultipleFornitori(selectedFornitoreIds).then(() => {
                  fetchFornitoriData();
                  setSelectedFornitoreIds([]);
                  if (table) table.toggleAllRowsSelected(false);
                });
              }
            }}
            table={table}
            searchValue={quickSearch}
            onSearchChange={(e) => setQuickSearch(e.target.value)}
            onResetFilters={handleResetFilters}
            hasFilters={hasManualFilters || reportType !== 'all'}
            activeFiltersCount={table.getState().columnFilters.length + (reportType !== 'all' ? 1 : 0)}
          />
        ) : (
        <Row className="align-items-center">
          <Col md={6} className="d-flex justify-content-start align-items-center">
            <Button as={Link} to="/nuovo/fornitore" className="me-2" size="sm" variant="success">Nuovo Fornitore</Button>
            <Dropdown className="me-2">
              <Dropdown.Toggle variant="primary" id="dropdown-import" size="sm">Importa</Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item as={Link} to="/fornitori/import">Importa da CSV/Excel</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown className="me-2">
              <Dropdown.Toggle variant="info" id="dropdown-export" size="sm">Esporta</Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={handleExportSelected} disabled={selectedFornitoreIds.length === 0 || exporting}>{exporting ? 'Esportazione...' : 'Esporta Selezionati (CSV)'}</Dropdown.Item>
                <Dropdown.Item onClick={handleExportAllExcel} disabled={exportingExcel}>{exportingExcel ? 'Esportazione...' : 'Esporta Tutto (Excel)'}</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown className="me-2">
              <Dropdown.Toggle variant="secondary" id="dropdown-actions" size="sm">Azioni</Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handlePrintWithLayout(1)} disabled={selectedFornitoreIds.length === 0}>Stampa con Layout</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
          <Col md={6} className="d-flex justify-content-end align-items-center">
            <Form.Group className="me-2"> {/* Filtro reportType */}
              <Form.Select onChange={(e) => setReportType(e.target.value)} value={reportType} size="sm">
                <option value="all">Tutti i Fornitori</option>
                <option value="with_piva">Con Partita IVA</option>
                <option value="without_piva">Senza Partita IVA</option>
              </Form.Select>
            </Form.Group>
            <Dropdown className="me-2" autoClose="outside">
              <Dropdown.Toggle variant="outline-secondary" id="dropdown-sorting" size="sm">Ordinamento</Dropdown.Toggle>
              <Dropdown.Menu className="long-dropdown-menu p-2">
                <SortController columns={table.getAllLeafColumns()} sorting={sorting} setSorting={setSorting} />
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown className="me-2" autoClose="outside">
              <Dropdown.Toggle variant="outline-secondary" id="dropdown-columns" size="sm">Colonne</Dropdown.Toggle>
              <Dropdown.Menu className="long-dropdown-menu p-0" style={{ minWidth: '500px' }}>
                <div className="d-flex">
                  <div className="p-2" style={{ flex: 1, borderRight: '1px solid #dee2e6' }}><ColumnOrderController table={table} allColumnsMap={allColumnsMap} /></div>
                  <div className="p-2" style={{ flex: 1 }}>
                    <h6 className="fw-bold">Mostra Colonne</h6>
                    <div className="d-flex flex-column">
                      {table.getAllColumns().filter(c => c.id !== 'select').map(c => <Form.Check key={c.id} type="checkbox" label={allColumnsMap[c.id]} checked={c.getIsVisible()} onChange={c.getToggleVisibilityHandler()} className="dropdown-item" />)}
                    </div>
                  </div>
                </div>
              </Dropdown.Menu>
            </Dropdown>
            <Button variant="outline-secondary" size="sm" className="ms-2" onClick={handleResetFilters} disabled={!hasManualFilters && reportType === 'all'}>Azzera Filtri</Button> {/* Aggiornato per reportType */}
          </Col>
        </Row>
        )}
      </Card.Header>
      <Card.Body>
        {!isMobile && (
          <Form.Control
            type="text"
            className="mb-3"
            placeholder="Cerca per denominazione, email, P.IVA..."
            value={quickSearch}
            onChange={e => setQuickSearch(e.target.value)}
          />
        )}
        {loading && <div className="text-center"><Spinner animation="border" /></div>}
        {error && <Alert variant="danger">{error}</Alert>}
        {!loading && !error && !isMobile && (
          <div style={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
            <Table striped bordered hover style={{ width: table.getTotalSize() }} className="aligned-table">
            <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <React.Fragment key={headerGroup.id}>
                    <tr>
                      {headerGroup.headers.map(header => (
                        <SortableHeader key={header.id} header={header} table={table} handleAutoResize={handleAutoResize} />
                      ))}
                    </tr>
                  </React.Fragment>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} onClick={() => handleRowClick(row)} className={row.getIsSelected() ? 'table-primary' : ''} style={{ cursor: 'pointer', height: '40px' }}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} style={{ verticalAlign: 'middle', width: `${cell.column.getSize()}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {!isMobile && (
          <ActionToolbar selectedItemIds={selectedFornitoreIds} entityType="fornitori" onDeleteSuccess={fetchFornitoriData} deleteApiMap={deleteApiMap} deleteMultipleApiMap={deleteMultipleApiMap} />
        )}
      </Card.Body>
      <Card.Footer className="d-flex justify-content-between align-items-center">
        <Form.Group as={Row} className="align-items-center mb-0">
          <Col xs="auto"><Form.Label className="mb-0">Record per pagina</Form.Label></Col>
          <Col xs="auto">
            <Form.Select size="sm" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
              <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
            </Form.Select>
          </Col>
        </Form.Group>
        <Pagination size="sm" className="mb-0">
          <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => setCurrentPage(prev => prev - 1)} disabled={currentPage === 1} />
          {renderPaginationItems()}
          <Pagination.Next onClick={() => setCurrentPage(prev => prev + 1)} disabled={currentPage === Math.ceil(totalFornitori / itemsPerPage)} />
          <Pagination.Last onClick={() => setCurrentPage(Math.ceil(totalFornitori / itemsPerPage))} disabled={currentPage === Math.ceil(totalFornitori / itemsPerPage)} />
        </Pagination>
      </Card.Footer>
    </Card>
  </>
  );
}

export default FornitoriList;